import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { MatCard } from '@angular/material/card';
import { MatButton } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { take, tap } from 'rxjs';
import { FormInlineErrorComponent } from '@k12/forms';
import { CoEnrollmentType, SubTask, Task, TaskTypeAction } from '@k12/models';
import { SchoolsService } from '@k12/school-services';

interface EnrollmentVerificationFormData {
  studentName?: string | null;
  studentPid?: string | null;
  schoolName?: string | null;
  studentEnrollmentId?: string | null;
  studentId?: string | null;
  householdId?: string | null;
  programPartnerId?: string | null;
  isFullTime?: boolean | null;
  coEnrollmentType?: string | number | null;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
  selector: 'lib-enrollment-verification-form',
  imports: [
    ReactiveFormsModule,
    MatInput,
    MatCard,
    MatButton,
    MatFormField,
    MatSelect,
    MatOption,
    MatSlideToggle,
    FormInlineErrorComponent
  ],
  templateUrl: './enrollment-verification-form.component.html',
  styleUrl: './enrollment-verification-form.component.scss'
})
export class EnrollmentVerificationFormComponent implements OnInit {
  @Input() task: Task | null;
  @Input() taskTypeAction: TaskTypeAction | null;
  @Input() taskAction: SubTask | null;
  @Output() submitFormData: EventEmitter<string> = new EventEmitter<string>();

  private readonly fb = inject(FormBuilder);
  private readonly schoolsService = inject(SchoolsService);
  private readonly destroyRef = inject(DestroyRef);

  protected form: FormGroup;
  protected context: EnrollmentVerificationFormData = {};
  protected coEnrollmentTypes: CoEnrollmentType[] = [];

  public ngOnInit(): void {
    const raw = this.taskAction?.formData ? (JSON.parse(this.taskAction.formData) as Record<string, unknown>) : {};
    this.context = this.normalizeKeys(raw);

    this.form = this.fb.group({
      studentName: [{ value: this.context.studentName ?? '', disabled: true }],
      studentPid: [{ value: this.context.studentPid ?? '', disabled: true }],
      schoolName: [{ value: this.context.schoolName ?? '', disabled: true }],
      isFullTime: [this.context.isFullTime ?? false],
      coEnrollmentType: [this.context.coEnrollmentType ?? null, this.coEnrollmentRequired]
    });

    this.form
      .get('isFullTime')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.form.get('coEnrollmentType')?.updateValueAndValidity());
    this.form.get('coEnrollmentType')?.updateValueAndValidity();

    this.getCoEnrollmentTypes();
    this.applyReadOnlyStatus();
  }

  private readonly coEnrollmentRequired = (control: AbstractControl): ValidationErrors | null => {
    const isFullTime = control.parent?.get('isFullTime')?.value === true;
    return isFullTime || control.value ? null : { required: true };
  };

  private normalizeKeys(raw: Record<string, unknown>): EnrollmentVerificationFormData {
    const entries = Object.entries(raw).map(([key, value]): [string, unknown] => [
      key.charAt(0).toLowerCase() + key.slice(1),
      value
    ]);
    return Object.fromEntries(entries) as EnrollmentVerificationFormData;
  }

  private getCoEnrollmentTypes(): void {
    this.schoolsService
      .getCoEnrollmentTypes()
      .pipe(
        take(1),
        tap((resp) => {
          const types = resp?.entity ?? [];
          this.coEnrollmentTypes = types.filter((t) => t?.isActive !== false);
        })
      )
      .subscribe();
  }

  private applyReadOnlyStatus(): void {
    const statusId = this.taskAction?.taskStatusId;
    if (statusId !== 1 && statusId !== 4) {
      this.form.disable();
    }
  }

  protected isFormReady(): boolean {
    return this.form.valid && this.form.dirty;
  }

  protected get isPartTime(): boolean {
    return this.form?.get('isFullTime')?.value !== true;
  }

  protected onBack(): void {
    globalThis.history.back();
  }

  protected submitForm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const isFullTime = raw.isFullTime === true;

    this.submitFormData.emit(
      JSON.stringify({
        ...this.context,
        isFullTime,
        coEnrollmentType: isFullTime ? null : (raw.coEnrollmentType as string | number | null)
      })
    );
  }
}
